import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import {
  Trash2, Copy, Plus, LogOut, BarChart3, Users, Mail, Building2,
  CheckCircle, Clock, X, ChevronDown, ChevronUp, Download, Upload,
  UserX, UserCheck, RefreshCw, History, Eye, PenLine, Send,
  FileSpreadsheet, FileText, ClipboardCopy, BellRing, Activity, FlaskConical, BookOpen, KeyRound,
  Rocket, CheckCircle2, XCircle
} from 'lucide-react';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

const STATUS_CONFIG = {
  invited: { label: 'Invited', cls: 'bg-slate-100 text-slate-600', icon: Send },
  opened: { label: 'Opened', cls: 'bg-blue-100 text-blue-700', icon: Eye },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  edited: { label: 'Edited', cls: 'bg-purple-100 text-purple-700', icon: PenLine }
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.invited;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

const ACTIVITY_LABELS = {
  member_created: { label: 'Member Created', cls: 'bg-emerald-50 text-emerald-700' },
  members_bulk_created: { label: 'Bulk Upload', cls: 'bg-emerald-50 text-emerald-700' },
  member_deleted: { label: 'Member Deleted', cls: 'bg-red-50 text-red-600' },
  invite_code_regenerated: { label: 'Code Regenerated', cls: 'bg-amber-50 text-amber-700' },
  member_deactivated: { label: 'Deactivated', cls: 'bg-slate-100 text-slate-600' },
  member_activated: { label: 'Activated', cls: 'bg-emerald-50 text-emerald-700' },
  test_flag_on: { label: 'Marked as Test', cls: 'bg-purple-50 text-purple-600' },
  test_flag_off: { label: 'Unmarked as Test', cls: 'bg-purple-50 text-purple-600' },
  data_purged: { label: 'Data Purged', cls: 'bg-red-100 text-red-700' },
  password_changed: { label: 'Password Changed', cls: 'bg-blue-50 text-blue-700' },
};

function parseCsv(text) {
  const rows = [];
  for (const rawLine of text.replace(/^\uFEFF/, '').split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const cells = line.split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
    const emailIdx = cells.findIndex(c => c.includes('@'));
    const email = emailIdx >= 0 ? cells[emailIdx] : '';
    const company = cells.find((c, i) => i !== emailIdx && c) || '';
    if (!company) continue;
    if (!email && /^(company\s*name|company|name|email|e-?mail)$/i.test(company)) continue;
    rows.push({ company_name: company, email });
  }
  return rows;
}

const REMINDER_DAY_OPTIONS = [3, 7, 14];

export default function AdminConsole() {
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMember, setNewMember] = useState({ company_name: '' });
  const [hideTest, setHideTest] = useState(false);
  const visibleMembers = hideTest ? members.filter(m => !m.is_test) : members;
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgeText, setPurgeText] = useState('');
  const [auditModal, setAuditModal] = useState(null);
  const [logFilter, setLogFilter] = useState({ action: 'all', from: '', to: '' });
  const [csvResult, setCsvResult] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [showReadiness, setShowReadiness] = useState(false);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [changeLogModal, setChangeLogModal] = useState(null);
  const [reminderDays, setReminderDays] = useState(7);
  const fileInputRef = useRef(null);
  const { logout } = useAuth();

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const res = await api.get('/api/admin/members');
      setMembers(res.data.members || []);
      setStats(res.data.stats || null);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const addMember = async () => {
    if (!newMember.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    try {
      await api.post('/api/admin/members', newMember);
      toast.success('Member added — Company Code and Invite Code generated');
      setShowAddModal(false);
      setNewMember({ company_name: '' });
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (rows.length === 0) {
      toast.error('No valid rows found. Expected columns: Company Name (required), Email (optional)');
      return;
    }
    try {
      const res = await api.post('/api/admin/members/bulk', { members: rows });
      const { created, skipped } = res.data;
      if (skipped.length > 0) {
        toast.warning(`${created.length} member(s) added — ${skipped.length} not uploaded`);
        setCsvResult({ created: created.length, skipped });
      } else {
        toast.success(`${created.length} member(s) added`);
      }
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV upload failed');
    }
  };

  const runReadinessCheck = async () => {
    setReadinessLoading(true);
    setShowReadiness(true);
    try {
      const res = await api.get('/api/admin/launch-readiness');
      setReadiness(res.data);
    } catch (error) {
      toast.error('Failed to run launch readiness check');
      setShowReadiness(false);
    } finally {
      setReadinessLoading(false);
    }
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setPwSaving(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success('Password changed successfully');
      setShowPwModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const viewActivityLog = async () => {
    try {
      const res = await api.get('/api/admin/activity-log');
      setLogFilter({ action: 'all', from: '', to: '' });
      setAuditModal(res.data.logs || []);
    } catch (error) {
      toast.error('Failed to load activity log');
    }
  };

  const filterLogs = (logs) => logs.filter(l => {
    if (logFilter.action !== 'all' && l.action !== logFilter.action) return false;
    const day = (l.at || '').slice(0, 10);
    if (logFilter.from && day < logFilter.from) return false;
    if (logFilter.to && day > logFilter.to) return false;
    return true;
  });

  const exportActivityCsv = (logs) => {
    const rows = [
      ['Action', 'Target', 'Details', 'Admin', 'When'].join(','),
      ...logs.map(l => [
        (ACTIVITY_LABELS[l.action] || { label: l.action }).label,
        `"${safeCell(l.target || '')}"`,
        `"${safeCell(l.details || '')}"`,
        safeCell(l.admin || ''),
        l.at || ''
      ].join(','))
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `benchmark_ph_activity_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${logs.length} activity record(s)`);
  };

  const purgeData = async () => {
    try {
      const res = await api.post('/api/admin/purge-data', { confirm: purgeText });
      const d = res.data.deleted;
      toast.success(`Purged: ${d.members} members, ${d.responses} responses, ${d.change_logs} change logs`);
      setShowPurgeModal(false);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purge failed');
    }
  };

  const regenerateCode = async (userId) => {
    if (!window.confirm('Regenerate this invite code? The old code will stop working.')) return;
    setActionLoading(userId);
    try {
      const res = await api.post(`/api/admin/members/${userId}/regenerate-code`);
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, invite_code: res.data.invite_code } : m));
      toast.success(`New code: ${res.data.invite_code}`);
    } catch (error) {
      toast.error('Failed to regenerate code');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleTest = async (userId, currentlyTest) => {
    setActionLoading(userId);
    try {
      const res = await api.post(`/api/admin/members/${userId}/toggle-test`);
      toast.success(res.data.is_test
        ? 'Marked as test — excluded from all statistics'
        : 'Unmarked as test — included in statistics again');
      loadMembers();
    } catch (error) {
      toast.error('Failed to update test flag');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleActive = async (userId, currentlyActive) => {
    const action = currentlyActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this member?`)) return;
    setActionLoading(userId);
    try {
      await api.post(`/api/admin/users/${userId}/${action}`);
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, is_active: !currentlyActive } : m));
      toast.success(`Member ${action}d`);
    } catch (error) {
      toast.error(`Failed to ${action} member`);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteMember = async (userId) => {
    if (!window.confirm('Delete this member and all their response data? This cannot be undone.')) return;
    setActionLoading(userId);
    try {
      await api.delete(`/api/admin/members/${userId}`);
      toast.success('Member deleted');
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    } finally {
      setActionLoading(null);
    }
  };

  const viewChangeLogs = async (member) => {
    try {
      const res = await api.get(`/api/admin/members/${member.user_id}/change-logs`);
      setChangeLogModal({ member, logs: res.data.logs || [] });
    } catch (error) {
      toast.error('Failed to load change logs');
    }
  };

  const daysSinceInvite = (m) => {
    if (!m.created_at) return 0;
    return Math.floor((Date.now() - new Date(m.created_at).getTime()) / 86400000);
  };

  const daysSinceActivity = (m) => {
    const ref = m.last_updated || m.started_at || m.created_at;
    if (!ref) return 0;
    return Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
  };

  const isOverdue = (m) => m.status === 'invited' && m.is_active && daysSinceInvite(m) >= reminderDays;
  const isStalled = (m) => m.status === 'in_progress' && m.is_active && daysSinceActivity(m) >= reminderDays;

  const buildFinishMessage = (m) => `Benchmark PH — IT-BPM ESG Pulse Navigator
ESG Double Materiality Industry Survey — Almost There!

Hi ${m.company_name},

Thank you for starting the IBPAP ESG Double Materiality Industry Survey — your progress has been saved. This is a friendly nudge to finish your remaining sections so your company's input counts toward the industry benchmarks.

Your access credentials:
Company Code: ${m.company_code}
Invite Code: ${m.invite_code}

Continue where you left off: ${window.location.origin}/auth

It only takes a few more minutes and you can save anytime. Thank you!`;

  const copyFinishNudge = (m) => {
    navigator.clipboard.writeText(buildFinishMessage(m));
    toast.success(`Finish-survey nudge for ${m.company_name} copied — ready to send!`);
  };

  const copyAllFinishNudges = () => {
    const stalled = members.filter(isStalled);
    if (stalled.length === 0) {
      toast.info('No stalled members to nudge');
      return;
    }
    const block = stalled.map(buildFinishMessage).join('\n\n' + '='.repeat(40) + '\n\n');
    navigator.clipboard.writeText(block);
    toast.success(`${stalled.length} finish-survey nudge(s) copied — ready to send!`);
  };

  const buildReminderMessage = (m) => `Benchmark PH — IT-BPM ESG Pulse Navigator
ESG Double Materiality Industry Survey — Friendly Reminder

Hi ${m.company_name},

This is a gentle reminder that your company's participation in the IBPAP ESG Double Materiality Industry Survey is still pending. Your input is important for building industry-wide ESG benchmarks.

Your access credentials:
Company Code: ${m.company_code}
Invite Code: ${m.invite_code}

Sign in here: ${window.location.origin}/auth

The survey takes about 20-30 minutes and can be saved and resumed anytime. Thank you!`;

  const copyReminder = (m) => {
    navigator.clipboard.writeText(buildReminderMessage(m));
    toast.success(`Reminder for ${m.company_name} copied — ready to send!`);
  };

  const copyAllReminders = () => {
    const overdue = members.filter(isOverdue);
    if (overdue.length === 0) {
      toast.info('No overdue members to remind');
      return;
    }
    const block = overdue.map(buildReminderMessage).join('\n\n' + '='.repeat(40) + '\n\n');
    navigator.clipboard.writeText(block);
    toast.success(`${overdue.length} reminder message(s) copied — ready to send!`);
  };

  const buildInviteMessage = (m) => `Benchmark PH — IT-BPM ESG Pulse Navigator
ESG Double Materiality Industry Survey — Invitation

Company: ${m.company_name}
Company Code: ${m.company_code}
Invite Code: ${m.invite_code}

Sign in here: ${window.location.origin}/auth

Use your Company Code and Invite Code to access the survey. Participation is by invitation only.`;

  const copyCredentials = (m) => {
    navigator.clipboard.writeText(buildInviteMessage(m));
    toast.success(`Credentials for ${m.company_name} copied — ready to send!`);
  };

  const copyAllInvites = () => {
    const pending = members.filter(m => m.status === 'invited' && m.is_active);
    if (pending.length === 0) {
      toast.info('No pending members — everyone has already opened the survey');
      return;
    }
    const block = pending.map(buildInviteMessage).join('\n\n' + '='.repeat(40) + '\n\n');
    navigator.clipboard.writeText(block);
    toast.success(`${pending.length} invite message(s) copied — ready to send!`);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  const downloadFile = async (endpoint, filename) => {
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const safeCell = (v) => {
    const s = String(v ?? '');
    return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  };

  const exportCsv = () => {
    const csvContent = [
      ['Company', 'Company Code', 'Email', 'Invite Code', 'Status', 'Response Code', 'First Login', 'Submitted At', 'Last Edited'].join(','),
      ...members.map(m => [
        `"${safeCell(m.company_name || '')}"`,
        m.company_code || '',
        safeCell(m.email || ''),
        m.invite_code || '',
        STATUS_CONFIG[m.status]?.label || m.status,
        m.response_code || '',
        m.first_login_at || '',
        m.submitted_at || '',
        m.last_edited_at || ''
      ].join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'benchmark_ph_members.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Members exported!');
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  const statCards = stats ? [
    { key: 'total', label: 'Total Members', value: stats.total, cls: 'text-primary' },
    { key: 'invited', label: 'Invited (Not Opened)', value: stats.invited, cls: 'text-slate-500' },
    { key: 'opened', label: 'Opened', value: stats.opened, cls: 'text-blue-600' },
    { key: 'in_progress', label: 'Tried / Not Completed', value: stats.in_progress, cls: 'text-amber-600' },
    { key: 'completed', label: 'Completed', value: stats.completed, cls: 'text-emerald-600' },
    { key: 'edited', label: 'Edited', value: stats.edited, cls: 'text-purple-600' }
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain" />
            <div>
              <h1 className="font-semibold text-primary">Participating Members</h1>
              <p className="text-xs text-slate-500">Manage invite codes & track survey responses</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/guide"
              className="text-slate-500 hover:text-primary p-2"
              title="Administrator Guide"
              data-testid="admin-guide-link"
            >
              <BookOpen className="w-5 h-5" />
            </Link>
            <button
              onClick={() => { setPwForm({ current_password: '', new_password: '', confirm: '' }); setShowPwModal(true); }}
              className="text-slate-500 hover:text-primary p-2"
              title="Change password"
              data-testid="change-password-btn"
            >
              <KeyRound className="w-5 h-5" />
            </button>
            <Link
              to="/admin/telemetry"
              className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
              data-testid="telemetry-link"
            >
              <Activity className="w-4 h-4" />
              Telemetry
            </Link>
            <Link
              to="/admin/dashboard"
              className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
              data-testid="dashboard-link"
            >
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </Link>
            <button
              onClick={logout}
              className="text-slate-500 hover:text-slate-700 p-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
        {/* Tracking Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="member-stats">
          {statCards.map(card => (
            <div key={card.key} className="dashboard-card !p-4" data-testid={`stat-${card.key}`}>
              <div className={`text-3xl font-bold ${card.cls}`}>{card.value}</div>
              <div className="text-xs text-slate-500 mt-1">{card.label}</div>
            </div>
          ))}
        </div>

        {stats && stats.total > 0 && (
          <div className="dashboard-card !p-4 mb-6" data-testid="completion-rate-bar">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">Survey Completion Rate</span>
              <span className="text-sm font-bold text-primary">{stats.completion_rate}%</span>
            </div>
            <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-accent rounded-full transition-all duration-500" style={{ width: `${stats.completion_rate}%` }} />
            </div>
          </div>
        )}

        {/* Reminder Tracker */}
        {(() => {
          const overdue = members.filter(isOverdue);
          const stalled = members.filter(isStalled);
          if (overdue.length === 0 && stalled.length === 0) return null;
          return (
            <div className="dashboard-card !p-4 mb-6 border-l-4 !border-l-amber-400 bg-amber-50/50 space-y-3" data-testid="reminder-banner">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <BellRing className="w-5 h-5 text-amber-500" />
                Reminders — no activity for
                <select
                  value={reminderDays}
                  onChange={(e) => setReminderDays(parseInt(e.target.value))}
                  className="px-2 py-0.5 rounded-lg border border-amber-300 bg-white text-sm font-semibold text-amber-700"
                  data-testid="reminder-days-select"
                >
                  {REMINDER_DAY_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                day{reminderDays > 1 ? 's' : ''}+
              </div>

              {overdue.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 pl-7" data-testid="overdue-invites-row">
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-sm text-slate-700" data-testid="reminder-count">
                      <span className="font-semibold">{overdue.length}</span> {overdue.length > 1 ? "haven't" : "hasn't"} opened their invite
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{overdue.map(m => m.company_name).join(', ')}</p>
                  </div>
                  <button
                    onClick={copyAllReminders}
                    className="h-9 px-4 flex items-center gap-2 text-sm font-semibold bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-full transition-all"
                    data-testid="copy-all-reminders-btn"
                  >
                    <ClipboardCopy className="w-4 h-4" />
                    Copy Invite Reminders
                  </button>
                </div>
              )}

              {stalled.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 pl-7" data-testid="stalled-surveys-row">
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-sm text-slate-700" data-testid="stalled-count">
                      <span className="font-semibold">{stalled.length}</span> started but {stalled.length > 1 ? "haven't" : "hasn't"} completed the survey
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{stalled.map(m => m.company_name).join(', ')}</p>
                  </div>
                  <button
                    onClick={copyAllFinishNudges}
                    className="h-9 px-4 flex items-center gap-2 text-sm font-semibold bg-orange-400 hover:bg-orange-500 text-slate-900 rounded-full transition-all"
                    data-testid="copy-all-nudges-btn"
                  >
                    <ClipboardCopy className="w-4 h-4" />
                    Copy Finish Nudges
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Members Table */}
        <div className="dashboard-card" data-testid="members-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="text-lg font-semibold text-primary">Members ({members.length})</h2>
            <div className="flex flex-wrap gap-2">
              <input
                type="file"
                accept=".csv,text/csv"
                ref={fileInputRef}
                onChange={handleCsvUpload}
                className="hidden"
                data-testid="csv-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
                data-testid="upload-csv-btn"
              >
                <Upload className="w-4 h-4" />
                Upload CSV
              </button>
              <button
                onClick={copyAllInvites}
                className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
                title="Copy invitation messages for all pending (not yet opened) members"
                data-testid="copy-all-invites-btn"
              >
                <ClipboardCopy className="w-4 h-4" />
                Copy All Invites
              </button>
              <button
                onClick={exportCsv}
                className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
                data-testid="export-members-btn"
              >
                <Download className="w-4 h-4" />
                CSV
              </button>
              <button
                onClick={() => downloadFile('/api/admin/export/excel', 'benchmark_ph_responses.xlsx')}
                className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>
              <button
                onClick={() => downloadFile('/api/admin/export/pdf', 'benchmark_ph_report.pdf')}
                className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
                data-testid="export-pdf-btn"
              >
                <FileText className="w-4 h-4" />
                PDF Report
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-accent h-10 px-4 flex items-center gap-2 text-sm"
                data-testid="add-member-btn"
              >
                <Plus className="w-4 h-4" />
                Add Member
              </button>
            </div>
          </div>

          {members.some(m => m.is_test) && (
            <label className="flex items-center gap-2 mb-3 text-sm text-slate-600 cursor-pointer w-fit" data-testid="hide-test-toggle">
              <input
                type="checkbox"
                checked={hideTest}
                onChange={(e) => setHideTest(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-accent focus:ring-accent"
              />
              Hide test members ({members.filter(m => m.is_test).length})
            </label>
          )}

          {members.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No participating members yet</p>
              <p className="text-sm">Add members manually or upload a CSV (Company Name required, Email optional)</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Company</th>
                    <th>Company Code</th>
                    <th>Invite Code</th>
                    <th>Status</th>
                    <th>Response Code</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleMembers.map((m, idx) => (
                    <React.Fragment key={m.user_id}>
                      <tr
                        className="cursor-pointer"
                        onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                        data-testid={`member-row-${idx}`}
                      >
                        <td>
                          {expandedRow === idx ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </td>
                        <td className="font-medium">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {m.company_name}
                            {m.is_test && (
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-[10px] font-semibold uppercase" data-testid={`test-badge-${idx}`}>Test</span>
                            )}
                            {!m.is_active && (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-semibold uppercase">Inactive</span>
                            )}
                          </div>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-mono font-semibold tracking-wider" data-testid={`company-code-${idx}`}>
                              {m.company_code || '—'}
                            </code>
                            {m.company_code && (
                              <button
                                onClick={() => copyCode(m.company_code)}
                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                title="Copy company code"
                                data-testid={`copy-company-code-${idx}`}
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono font-semibold tracking-wider" data-testid={`invite-code-${idx}`}>
                              {m.invite_code}
                            </code>
                            <button
                              onClick={() => copyCode(m.invite_code)}
                              className="p-1.5 text-slate-400 hover:text-accent-dark hover:bg-accent/10 rounded-lg transition-all"
                              title="Copy code"
                              data-testid={`copy-code-${idx}`}
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td data-testid={`member-status-${idx}`}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <StatusBadge status={m.status} />
                            {isOverdue(m) && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
                                title={`Invited ${daysSinceInvite(m)} days ago, not yet opened`}
                                data-testid={`overdue-badge-${idx}`}
                              >
                                <BellRing className="w-3 h-3" />
                                {daysSinceInvite(m)}d
                              </span>
                            )}
                            {isStalled(m) && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700"
                                title={`No survey activity for ${daysSinceActivity(m)} days`}
                                data-testid={`stalled-badge-${idx}`}
                              >
                                <BellRing className="w-3 h-3" />
                                {daysSinceActivity(m)}d idle
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {m.response_code ? (
                            <code className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-xs font-mono" data-testid={`response-code-${idx}`}>
                              {m.response_code}
                            </code>
                          ) : (
                            <span className="text-slate-300 text-sm">—</span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                            {isOverdue(m) && (
                              <button
                                onClick={() => copyReminder(m)}
                                className="p-2 text-amber-500 hover:bg-amber-50 hover:text-amber-700 rounded-lg transition-all"
                                title="Copy reminder message"
                                data-testid={`copy-reminder-${idx}`}
                              >
                                <BellRing className="w-4 h-4" />
                              </button>
                            )}
                            {isStalled(m) && (
                              <button
                                onClick={() => copyFinishNudge(m)}
                                className="p-2 text-orange-500 hover:bg-orange-50 hover:text-orange-700 rounded-lg transition-all"
                                title="Copy finish-survey nudge"
                                data-testid={`copy-nudge-${idx}`}
                              >
                                <BellRing className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => copyCredentials(m)}
                              className="p-2 text-accent-dark hover:bg-accent/10 rounded-lg transition-all"
                              title="Copy credentials (invite message)"
                              data-testid={`copy-credentials-${idx}`}
                            >
                              <ClipboardCopy className="w-4 h-4" />
                            </button>
                            {m.edit_count > 0 && (
                              <button
                                onClick={() => viewChangeLogs(m)}
                                className="p-2 text-purple-500 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-all relative"
                                title={`View change log (${m.edit_count})`}
                                data-testid={`view-changes-${idx}`}
                              >
                                <History className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => regenerateCode(m.user_id)}
                              disabled={actionLoading === m.user_id}
                              className="p-2 text-slate-500 hover:bg-slate-100 hover:text-primary rounded-lg transition-all"
                              title="Regenerate invite code"
                              data-testid={`regen-code-${idx}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => toggleTest(m.user_id, m.is_test)}
                              disabled={actionLoading === m.user_id}
                              className={`p-2 rounded-lg transition-all ${
                                m.is_test
                                  ? 'text-purple-600 bg-purple-50 hover:bg-purple-100'
                                  : 'text-slate-400 hover:bg-purple-50 hover:text-purple-600'
                              }`}
                              title={m.is_test ? 'Unmark as test (include in stats)' : 'Mark as test (exclude from stats)'}
                              data-testid={`toggle-test-${idx}`}
                            >
                              <FlaskConical className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => toggleActive(m.user_id, m.is_active)}
                              disabled={actionLoading === m.user_id}
                              className={`p-2 rounded-lg transition-all ${
                                m.is_active
                                  ? 'text-amber-500 hover:bg-amber-50 hover:text-amber-700'
                                  : 'text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700'
                              }`}
                              title={m.is_active ? 'Deactivate member' : 'Activate member'}
                              data-testid={`toggle-member-${idx}`}
                            >
                              {m.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => deleteMember(m.user_id)}
                              disabled={actionLoading === m.user_id}
                              className="p-2 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all"
                              title="Delete member"
                              data-testid={`delete-member-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRow === idx && (
                        <tr>
                          <td colSpan="7" className="bg-slate-50 p-4">
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm mb-4">
                              <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wide">Email</p>
                                <p className="text-slate-700 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" />{m.email || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wide">Invited On</p>
                                <p className="text-slate-700">{fmtDate(m.created_at)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wide">First Login</p>
                                <p className="text-slate-700">{fmtDate(m.first_login_at)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wide">Submitted</p>
                                <p className="text-slate-700">{fmtDate(m.submitted_at)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wide">Last Edited</p>
                                <p className="text-slate-700">{fmtDate(m.last_edited_at)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 uppercase tracking-wide">Compliance Accepted</p>
                                <p className={m.compliance_accepted_at ? 'text-emerald-700 font-medium' : 'text-slate-400'} data-testid={`compliance-accepted-${idx}`}>
                                  {m.compliance_accepted_at ? fmtDate(m.compliance_accepted_at) : 'Not yet'}
                                </p>
                              </div>
                            </div>
                            {m.response ? (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm border-t border-slate-200 pt-4">
                                <div>
                                  <h4 className="font-semibold text-slate-700 mb-2">Section A - Profile</h4>
                                  <p>Employees: {m.response.section_a?.total_employees || '-'}</p>
                                  <p>Segment: {m.response.section_a?.business_segment || '-'}</p>
                                  <p>Sites: {m.response.section_a?.operational_sites || '-'}</p>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-slate-700 mb-2">Section C - Key KPIs</h4>
                                  <p>Attrition: {m.response.section_c?.attrition_rate || '-'}%</p>
                                  <p>Renewable Energy: {m.response.section_c?.renewable_energy_pct || '-'}%</p>
                                  <p>Local Hiring: {m.response.section_c?.local_hiring_pct || '-'}%</p>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-slate-700 mb-2">Section D - Forward Looking</h4>
                                  <p>Has ESG Targets: {m.response.section_d?.has_esg_targets ? 'Yes' : 'No'}</p>
                                  <p>2030 Carbon Target: {m.response.section_d?.carbon_target_2030 || '-'}%</p>
                                  <p>Biggest Risk: {m.response.section_d?.biggest_risk || '-'}</p>
                                </div>
                              </div>
                            ) : (
                              <p className="text-slate-400 text-sm border-t border-slate-200 pt-4">No survey response yet</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Danger Zone */}
        <div className="mt-8 bg-white rounded-2xl border border-red-200 p-6" data-testid="danger-zone">
          <h2 className="text-sm font-bold text-red-600 uppercase tracking-wide mb-2">Danger Zone</h2>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-sm text-slate-500">
              Permanently delete <strong>all members, responses, change logs and audit records</strong> for a clean testing round. Admin accounts are kept. This cannot be undone.
            </p>
            <div className="flex flex-shrink-0 gap-2">
              <button
                onClick={runReadinessCheck}
                className="h-10 px-4 rounded-xl border border-accent/40 text-accent-dark text-sm font-semibold hover:bg-accent/5 transition-all flex items-center gap-2"
                data-testid="launch-readiness-btn"
              >
                <Rocket className="w-4 h-4" />
                Launch Readiness
              </button>
              <button
                onClick={viewActivityLog}
                className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all flex items-center gap-2"
                data-testid="activity-log-btn"
              >
                <History className="w-4 h-4" />
                Activity Log
              </button>
              <button
                onClick={() => { setPurgeText(''); setShowPurgeModal(true); }}
                className="h-10 px-4 rounded-xl border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition-all"
                data-testid="purge-data-btn"
              >
                Purge all data…
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Launch Readiness Modal */}
      {showReadiness && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg animate-fade-in max-h-[85vh] overflow-y-auto" data-testid="launch-readiness-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                <Rocket className="w-5 h-5 text-accent-dark" />
                Launch Readiness Check
              </h3>
              <button onClick={() => setShowReadiness(false)} className="text-slate-400 hover:text-slate-600" data-testid="close-readiness-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            {readinessLoading || !readiness ? (
              <div className="py-10 text-center text-sm text-slate-400">Running checks…</div>
            ) : (
              <>
                <div
                  className={`rounded-xl p-4 mb-4 flex items-center gap-3 ${readiness.ready ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}
                  data-testid="readiness-status"
                >
                  {readiness.ready ? <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" /> : <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />}
                  <div>
                    <p className={`font-bold text-sm ${readiness.ready ? 'text-emerald-700' : 'text-red-600'}`}>
                      {readiness.ready ? 'Ready for launch' : 'Not ready for launch'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {readiness.ready
                        ? 'All pre-launch checks passed on this environment.'
                        : 'Resolve the failed items below, then re-run the check.'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  {readiness.checks.map((c) => (
                    <div
                      key={c.id}
                      className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${c.passed ? 'border-emerald-100 bg-emerald-50/50' : 'border-red-200 bg-red-50'}`}
                      data-testid={`readiness-check-${c.id}`}
                    >
                      {c.passed
                        ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                        : <XCircle className="w-4 h-4 mt-0.5 text-red-500 flex-shrink-0" />}
                      <div>
                        <p className={`font-medium ${c.passed ? 'text-slate-700' : 'text-red-700'}`}>{c.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{c.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mb-4" data-testid="readiness-meta">
                  {readiness.member_count} participating member(s) currently invited · Checked {new Date(readiness.checked_at).toLocaleString()} · This check runs against <strong>this environment only</strong> — re-run it on production after deploying.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowReadiness(false)} className="flex-1 btn-secondary h-11">
                    Close
                  </button>
                  <button
                    onClick={runReadinessCheck}
                    className="flex-1 btn-accent h-11 flex items-center justify-center gap-2"
                    data-testid="rerun-readiness-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Re-run check
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-fade-in" data-testid="add-member-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-primary">Add Participating Member</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                <input
                  type="text"
                  value={newMember.company_name}
                  onChange={(e) => setNewMember({ ...newMember, company_name: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20"
                  placeholder="e.g., Acme BPO Inc."
                  data-testid="member-company-input"
                />
              </div>
              <p className="text-xs text-slate-400">The Company Code and a random 6-character Invite Code will be generated automatically.</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="flex-1 btn-secondary">
                Cancel
              </button>
              <button onClick={addMember} className="flex-1 btn-accent" data-testid="confirm-add-member-btn">
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Log Modal */}
      {changeLogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto animate-fade-in" data-testid="change-log-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-primary">Response Change Log</h3>
                <p className="text-sm text-slate-500">
                  {changeLogModal.member.company_name} — changes vs. original submission
                  {changeLogModal.member.response_code && (
                    <code className="ml-2 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs font-mono">
                      {changeLogModal.member.response_code}
                    </code>
                  )}
                </p>
              </div>
              <button onClick={() => setChangeLogModal(null)} className="text-slate-400 hover:text-slate-600" data-testid="close-change-log">
                <X className="w-5 h-5" />
              </button>
            </div>
            {changeLogModal.logs.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No changes logged</p>
            ) : (
              <div className="space-y-4">
                {changeLogModal.logs.map((log, li) => (
                  <div key={li} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`change-log-entry-${li}`}>
                    <div className="bg-slate-50 px-4 py-2 flex items-center gap-2 text-sm">
                      <History className="w-4 h-4 text-purple-500" />
                      <span className="font-medium text-slate-700">{new Date(log.changed_at).toLocaleString()}</span>
                      <span className="text-slate-400">· {log.changes.length} field(s) differ from original</span>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-slate-400 uppercase">
                          <th className="px-4 py-2">Section</th>
                          <th className="px-4 py-2">Field</th>
                          <th className="px-4 py-2">Original</th>
                          <th className="px-4 py-2">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {log.changes.map((c, ci) => (
                          <tr key={ci} className="border-t border-slate-100">
                            <td className="px-4 py-2 uppercase text-xs font-semibold text-slate-500">{c.section.replace('section_', '')}</td>
                            <td className="px-4 py-2 font-medium text-slate-700">{c.field}</td>
                            <td className="px-4 py-2 text-red-600 line-through decoration-red-300">{JSON.stringify(c.original) ?? '—'}</td>
                            <td className="px-4 py-2 text-emerald-700 font-medium">{JSON.stringify(c.new) ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      {/* Admin activity log modal */}
      {auditModal !== null && (() => {
        const filteredLogs = filterLogs(auditModal);
        return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="activity-log-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold text-primary flex items-center gap-2">
                <History className="w-5 h-5 text-accent-dark" />
                Admin Activity Log
              </h3>
              <button
                onClick={() => setAuditModal(null)}
                className="text-slate-400 hover:text-slate-600 p-1"
                data-testid="activity-log-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Every sensitive admin action is recorded here — member changes, code regenerations, flag changes, purges and password changes.
            </p>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <select
                value={logFilter.action}
                onChange={(e) => setLogFilter({ ...logFilter, action: e.target.value })}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 bg-white"
                data-testid="activity-filter-action"
              >
                <option value="all">All actions</option>
                {Object.entries(ACTIVITY_LABELS).map(([value, meta]) => (
                  <option key={value} value={value}>{meta.label}</option>
                ))}
              </select>
              <input
                type="date"
                value={logFilter.from}
                onChange={(e) => setLogFilter({ ...logFilter, from: e.target.value })}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600"
                title="From date"
                data-testid="activity-filter-from"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={logFilter.to}
                onChange={(e) => setLogFilter({ ...logFilter, to: e.target.value })}
                className="h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600"
                title="To date"
                data-testid="activity-filter-to"
              />
              <button
                onClick={() => exportActivityCsv(filteredLogs)}
                disabled={filteredLogs.length === 0}
                className="ml-auto h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-40"
                data-testid="activity-export-csv-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV ({filteredLogs.length})
              </button>
            </div>
            {filteredLogs.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm" data-testid="activity-log-empty">
                {auditModal.length === 0 ? 'No activity recorded yet.' : 'No records match the selected filters.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-200">
                      <th className="px-3 py-2">Action</th>
                      <th className="px-3 py-2">Target / Details</th>
                      <th className="px-3 py-2">Admin</th>
                      <th className="px-3 py-2">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log, i) => {
                      const meta = ACTIVITY_LABELS[log.action] || { label: log.action, cls: 'bg-slate-100 text-slate-600' };
                      return (
                        <tr key={i} className="border-b border-slate-100 align-top" data-testid={`activity-row-${i}`}>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                          </td>
                          <td className="px-3 py-2">
                            {log.target && <span className="font-medium text-slate-700">{log.target}</span>}
                            {log.details && <span className="block text-xs text-slate-400 mt-0.5">{log.details}</span>}
                            {!log.target && !log.details && <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{log.admin || '—'}</td>
                          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{fmtDate(log.at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* CSV upload results modal */}
      {csvResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="csv-result-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-primary mb-1">Bulk Upload Results</h3>
            <div className="flex gap-3 my-4">
              <div className="flex-1 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700" data-testid="csv-added-count">{csvResult.created}</p>
                <p className="text-xs text-emerald-700">Added</p>
              </div>
              <div className="flex-1 rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
                <p className="text-2xl font-bold text-amber-700" data-testid="csv-skipped-count">{csvResult.skipped.length}</p>
                <p className="text-xs text-amber-700">Not uploaded</p>
              </div>
            </div>
            <p className="text-sm font-semibold text-slate-600 mb-2">Rows not uploaded and why:</p>
            <div className="space-y-2 mb-5">
              {csvResult.skipped.map((s, i) => (
                <div key={i} className="p-3 rounded-xl border border-amber-200 bg-amber-50/50 text-sm" data-testid={`csv-skipped-row-${i}`}>
                  <p className="font-medium text-slate-700">
                    {s.company_name || '(no company name)'}
                    {s.email && <span className="ml-2 text-xs text-slate-400">{s.email}</span>}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">{s.reason}</p>
                </div>
              ))}
            </div>
            <button
              onClick={() => setCsvResult(null)}
              className="w-full btn-secondary h-11"
              data-testid="csv-result-close-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Change password modal */}
      {showPwModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="change-password-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-primary mb-1">Change Password</h3>
            <p className="text-sm text-slate-500 mb-4">
              At least 10 characters, with uppercase, lowercase and a number.
            </p>
            <div className="space-y-3 mb-4">
              <input
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-accent"
                placeholder="Current password"
                data-testid="current-password-input"
              />
              <input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-accent"
                placeholder="New password"
                data-testid="new-password-input"
              />
              <input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-accent"
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPwModal(false)}
                className="flex-1 btn-secondary h-11"
                data-testid="pw-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={changePassword}
                disabled={pwSaving || !pwForm.current_password || !pwForm.new_password || !pwForm.confirm}
                className="flex-1 btn-accent h-11 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pw-save-btn"
              >
                {pwSaving ? 'Saving…' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purge confirmation modal */}
      {showPurgeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="purge-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-red-600 mb-2">Purge all data?</h3>
            <p className="text-sm text-slate-500 mb-4">
              This permanently deletes <strong>every member, response, change log and audit record</strong>.
              Admin accounts, telemetry and settings are kept. Type <strong>PURGE</strong> to confirm.
            </p>
            <input
              type="text"
              value={purgeText}
              onChange={(e) => setPurgeText(e.target.value.toUpperCase())}
              className="w-full h-12 px-4 rounded-xl border border-red-200 focus:border-red-400 font-mono tracking-widest mb-4"
              placeholder="Type PURGE"
              data-testid="purge-confirm-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowPurgeModal(false)}
                className="flex-1 btn-secondary h-11"
                data-testid="purge-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={purgeData}
                disabled={purgeText !== 'PURGE'}
                className="flex-1 h-11 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                data-testid="purge-execute-btn"
              >
                Purge everything
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a 
          href="https://sustek.ai/solutions/soctech" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-emerald-600 hover:text-emerald-700 hover:underline"
        >
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
